import React, { useEffect, useState } from "react";
import {
	Box,
	Flex,
	Text,
	Input,
	InputGroup,
	InputLeftElement,
	Checkbox,
	VStack,
	Spinner,
	IconButton,
	Button,
	Menu,
	MenuButton,
	MenuDivider,
	MenuList,
	MenuItem,
	HStack,
	Icon,
	Tabs,
	TabList,
	Tab,
	TabPanels,
	TabPanel,
} from "@chakra-ui/react";
import { FiSearch, FiTrash2, FiPlus, FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { fetchListOfChatSessions, deleteChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import showToast from "hooks/useCustomToast";
import { DarwinHeader, SortBy, SortOrder } from "build-ts/common";
import { ChatClientType } from "build-ts/chat_session";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { Header } from "build-ts/metadata";
import { useAnalystChat } from "context/AnalystChatContext";
import { BsRobot } from "react-icons/bs";
import { ChevronDownIcon } from "@chakra-ui/icons";
import AnalysesTab from "components/sessionPage/AnalysesTab";
import AnalysisRun from "components/sessionPage/AnalysisRun";
import { IoChatbubblesOutline } from "react-icons/io5";
import { MdOutlineAssessment, MdSchedule } from "react-icons/md";
import { LuWorkflow } from "react-icons/lu";

const ChatSessionPage: React.FC = () => {
	const navigate = useNavigate();
	const { defaultTimeZone, defaultAnalystId, userId } = useUser();
	const { analystsList, fetchChatSessionsList, setChatSessionsList, isLoadingAnalysts } = useAnalystChat();

	// State
	const [chatSessions, setChatSessions] = useState<DarwinHeader[]>([]);
	const [totalChats, setTotalChats] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [hoveredId, setHoveredId] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Analyst filter state (for Chats tab)
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);

	// Inline rename state
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editValue, setEditValue] = useState("");

	// tab index state
	const [tabIndex, setTabIndex] = useState(0);

	// Wait for analysts to finish loading, then auto-select the default analyst and fetch sessions filtered by it.
	// If there's no defaultAnalystId, fetch all sessions once analysts are done loading.
	useEffect(() => {
		if (isLoadingAnalysts) return;

		if (defaultAnalystId) {
			const analyst = analystsList.find(a => a.id === defaultAnalystId) || null;
			setSelectedAnalyst(analyst);
			fetchChatSessions(undefined, analyst);
		} else {
			fetchChatSessions();
		}
	}, [defaultAnalystId, analystsList, isLoadingAnalysts]);

	const fetchChatSessions = async (search?: string, analystFilter?: Header | null, offset: number = 0, append: boolean = false) => {
		if (append) {
			setIsLoadingMore(true);
		} else {
			setIsLoading(true);
		}
		try {
			// Build associatedEntityKey if analyst is selected
			const associatedEntityKey = analystFilter ? [`ANALYST::${analystFilter.id}`] : [];

			const result = await fetchListOfChatSessions(
				[ChatClientType.FRIDA],
				50,
				offset,
				search,
				undefined,
				associatedEntityKey,
				SortBy.LAST_MODIFIED_DATE,
				SortOrder.DESC,
				userId
			);

			if (append) {
				setChatSessions(prev => [...prev, ...result.headers]);
				setTotalChats(prev => prev + result.headers.length);
			} else {
				setChatSessions(result.headers);
				setTotalChats(result.total ?? result.headers.length);
			}
		} catch (error) {
			console.error("Failed to fetch chat sessions:", error);
		} finally {
			setIsLoading(false);
			setIsLoadingMore(false);
		}
	};

	// Load more chat sessions
	const handleLoadMore = () => {
		fetchChatSessions(searchQuery || undefined, selectedAnalyst, chatSessions.length, true);
	};

	// Handle rename
	const handleRename = async (sessionId: string) => {
		if (!editValue.trim()) {
			setEditingId(null);
			return;
		}
		const success = await updateChatSessionHeaders([sessionId], editValue.trim(), undefined, false);
		if (success) {
			showToast("Chat renamed", "", "success");
			fetchChatSessions(searchQuery || undefined, selectedAnalyst);
			fetchChatSessionsList(ChatClientType.FRIDA); // Refresh the chat sessions list of Navbar
		}
		setEditingId(null);
	};

	// Handle analyst selection (Chats tab)
	const handleAnalystSelect = (analyst: Header | null) => {
		setSelectedAnalyst(analyst);
		fetchChatSessions(searchQuery || undefined, analyst);
	};

	// Handle search on Enter key press
	const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			fetchChatSessions(searchQuery || undefined, selectedAnalyst);
		}
	};

	// Format date using timezone from user context
	const formatLastModified = (timestamp?: number): string => {
		if (!timestamp) return "";
		return epochConverterWithTimeZone(timestamp, "d-MMM-yyyy, HH:mm", defaultTimeZone) + " " + defaultTimeZone.toUpperCase();
	};

	// Handle selection
	const toggleSelection = (id: string) => {
		setSelectedIds((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	const toggleSelectAll = () => {
		if (selectedIds.size === chatSessions.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(chatSessions.map((s) => s.id!).filter(Boolean)));
		}
	};

	// Handle delete
	const handleDeleteSelected = async () => {
		if (selectedIds.size === 0) return;

		setIsDeleting(true);
		try {
			await Promise.all(
				Array.from(selectedIds).map((id) => deleteChatSession(id))
			);
			const deletedIds = new Set(selectedIds);
			setChatSessions(prev => prev.filter(s => !deletedIds.has(s.id!)));
			setTotalChats(prev => prev - deletedIds.size);
			// Remove from context list if present (keeps Navbar in sync)
			setChatSessionsList(prev => prev.filter(s => !deletedIds.has(s.id!)));
			setSelectedIds(new Set());
		} catch (error) {
			showToast("Failed to delete chat sessions", "", "error");
			console.error("Failed to delete chat sessions:", error);
		} finally {
			setIsDeleting(false);
		}
	};

	// Handle chat click - open in new tab
	const handleChatClick = (id: string) => {
		window.open(`/chat/${id}`, "_blank");
	};

	const isAllSelected = chatSessions.length > 0 && selectedIds.size === chatSessions.length;

	return (
		<Box
			h="100vh"
			w="100%"
			bg="white"
			overflow="hidden"
		>
			<Flex h="100%" w="100%" justify="center">
				{/* Main Content */}
				<Box w="100%" maxW={tabIndex === 0 ? "1000px" : "1000px"} h="100%" px={5} py={6} display="flex" flexDirection="column" minH={0}>
					<Tabs
						mt={6}
						flex={1}
						display="flex"
						flexDirection="column"
						minH={0}
						colorScheme="purple"
						variant="line"
						index={tabIndex}
						onChange={(index) => setTabIndex(index)}
					>
						{/* Tab Headers */}
						<Flex justify="space-between" align="center" mb={6}>
							<TabList border="none" gap={7}>
								<Tab
									px={0}
									pb={2}
									fontSize="xl"
									fontWeight="600"
									color="gray.500"
									_focus={{ bg: "transparent", boxShadow: "none" }}
									_active={{ bg: "transparent" }}
									_selected={{
										color: "purple.700",
										borderColor: "purple.700",
										borderBottomWidth: "3px",
										fontWeight: "700",
									}}
									_hover={{ color: "purple.700" }}
								>
									<HStack spacing={2}>
										<Icon as={IoChatbubblesOutline} boxSize={6} />
										<Text fontSize="xl" fontWeight="600" mb={0}>Chats</Text>
									</HStack>
								</Tab>
								<Tab
									px={0}
									pb={2}
									fontSize="xl"
									fontWeight="600"
									color="gray.500"
									_focus={{ bg: "transparent", boxShadow: "none" }}
									_active={{ bg: "transparent" }}
									_selected={{
										color: "purple.700",
										borderColor: "purple.700",
										borderBottomWidth: "3px",
										fontWeight: "700",
									}}
									_hover={{ color: "purple.700" }}
								>
									<HStack spacing={2}>
										<Icon as={LuWorkflow} boxSize={6} />
										<Text fontSize="xl" fontWeight="600" mb={0}>Workflows</Text>
									</HStack>
								</Tab>
								{/* <Tab
									px={0}
									pb={2}
									fontSize="xl"
									fontWeight="600"
									color="gray.500"
									_focus={{ bg: "transparent", boxShadow: "none" }}
									_active={{ bg: "transparent" }}
									_selected={{
										color: "purple.700",
										borderColor: "purple.700",
										borderBottomWidth: "3px",
										fontWeight: "700",
									}}
									_hover={{ color: "purple.700" }}
								>
									<HStack spacing={2}>
										<Icon as={MdSchedule} boxSize={6} />
										<Text fontSize="xl" fontWeight="600" mb={0}>Scheduled Runs</Text>
									</HStack>
								</Tab> */}
								<Tab
									px={0}
									pb={2}
									fontSize="xl"
									fontWeight="600"
									color="gray.500"
									_focus={{ bg: "transparent", boxShadow: "none" }}
									_active={{ bg: "transparent" }}
									_selected={{
										color: "purple.700",
										borderColor: "purple.700",
										borderBottomWidth: "3px",
										fontWeight: "700",
									}}
									_hover={{ color: "purple.700" }}
								>
									<HStack spacing={2}>
										<Icon as={MdSchedule} boxSize={6} />
										<Text fontSize="xl" fontWeight="600" mb={0}>Workflow Runs</Text>
									</HStack>
								</Tab>
							</TabList>
							<Button
								leftIcon={<Icon as={FiPlus} boxSize={5} />}
								bg="purple.700"
								color="white"
								size="md"
								fontWeight="600"
								borderRadius="md"
								_hover={{ bg: "purple.600" }}
								onClick={() => navigate("/chat")}
							>
								New chat
							</Button>
						</Flex>

						<TabPanels flex={1} display="flex" flexDirection="column" minH={0}>
							{/* Chats Tab */}
							<TabPanel px={0} py={0} flex={1} display="flex" flexDirection="column" minH={0}>
								<Box maxW="700px" w="100%" mx="auto" display="flex" flexDirection="column" flex={1} minH={0}>
									{/* Search Bar */}
									<InputGroup mb={5} size="md">
										<InputLeftElement pointerEvents="none" h="40px">
											<Icon as={FiSearch} boxSize={5} color="purple.700" />
										</InputLeftElement>
										<Input
											placeholder="Search chats..."
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											onKeyDown={handleSearchKeyDown}
											bg="white"
											border="1px solid"
											borderColor="purple.800"
											borderRadius="lg"
											fontSize="md"
											color="fg.default"
											_placeholder={{ color: "purple.800" }}
											_focus={{
												borderColor: "purple.700",
												boxShadow: "none",
												borderWidth: "2px",
												bg: "purple.50",
											}}
										/>
									</InputGroup>

									{/* Selection Actions */}
									{selectedIds.size > 0 && (
										<Flex
											mb={2}
											pl={3}
											pr={1}
											py={0}
											bg="gray.100"
											borderRadius="md"
											align="center"
											justify="space-between"
										>
											<Text fontSize="sm" color="fg.default" fontWeight="600" align="left" m={0}>
												{selectedIds.size} chat{selectedIds.size > 1 ? "s" : ""} selected
											</Text>
											<Button
												leftIcon={<Icon as={FiTrash2} boxSize={4} />}
												size="xs"
												colorScheme="red"
												variant="ghost"
												isLoading={isDeleting}
												onClick={handleDeleteSelected}
												fontWeight="700"
											>
												Delete
											</Button>
										</Flex>
									)}

									{/* Chat Count & Select All & Filter */}
									<Flex align="center" justify="space-between" mb={3} px={1}>
										<HStack spacing={2}>
											<Checkbox
												isChecked={isAllSelected}
												onChange={toggleSelectAll}
												colorScheme="gray"
												size="md"
												m={0}
											/>
											<Text fontSize="sm" color="gray.500" fontWeight="600" align="left" m={0}>
												Showing {totalChats} chat{totalChats !== 1 ? "s" : ""}
											</Text>
										</HStack>

										{/* Analyst Filter */}
										<Menu>
											<MenuButton
												as={Button}
												size="sm"
												variant="outline"
												colorScheme="purple"
												bg="purple.50"
												borderColor="purple.50"
												_hover={{
													bg: "purple.100",
													borderColor: "purple.100"
												}}
												borderRadius="full"
												borderWidth="1px"
												px={3}
												py={1}
											>
												<HStack spacing={2}>
													<Icon as={BsRobot} boxSize={5} />
													<Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
														{selectedAnalyst ? selectedAnalyst.name : "All analysts"}
													</Text>
													<Icon as={ChevronDownIcon} boxSize={5} />
												</HStack>
											</MenuButton>
											<MenuList
												maxH="250px"
												overflowY="auto"
												minW="200px"
												borderColor="purple.200"
												boxShadow="lg"
											>
												<MenuItem
													fontSize="sm"
													bg={!selectedAnalyst ? "purple.700" : "transparent"}
													_hover={{ bg: !selectedAnalyst ? "purple.600" : "purple.50" }}
													onClick={() => handleAnalystSelect(null)}
												>
													<HStack spacing={2}>
														<Icon as={BsRobot} boxSize={5} color={!selectedAnalyst ? "white" : "purple.700"} />
														<Text fontSize="sm" color={!selectedAnalyst ? "white" : "fg.default"} fontWeight="500" m={0}>
															All analysts
														</Text>
													</HStack>
												</MenuItem>
												{analystsList.length > 0 && <MenuDivider />}
												{analystsList.length === 0 ? (
													<MenuItem isDisabled fontSize="sm" color="purple.500">
														No analysts found
													</MenuItem>
												) : (
													analystsList.map((analyst) => (
														<MenuItem
															key={analyst.id}
															fontSize="sm"
															bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
															_hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
															onClick={() => handleAnalystSelect(analyst)}
														>
															<HStack spacing={2}>
																<Icon as={BsRobot} boxSize={5} color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"} />
																<Text fontSize="sm" color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"} fontWeight="500" m={0} noOfLines={1}>
																	{analyst.name}
																</Text>
															</HStack>
														</MenuItem>
													))
												)}
											</MenuList>
										</Menu>
									</Flex>

									{/* Chat List */}
									<Box
										// flex={1}
										minH={0}
										overflowY="auto"
										css={{
											"&::-webkit-scrollbar": {
												width: "4px",
											},
											"&::-webkit-scrollbar-track": {
												background: "transparent",
											},
											"&::-webkit-scrollbar-thumb": {
												background: "#dfc2f0",
												borderRadius: "2px",
											},
										}}
										maxW="700px"
										alignItems="center"
									>
										{isLoading ? (
											<Flex justify="center" align="center" h="150px">
												<Spinner size="md" color="purple.700" thickness="2px" />
											</Flex>
										) : chatSessions.length === 0 ? (
											<Flex
												direction="column"
												align="center"
												justify="center"
												h="150px"
												color="gray.500"
											>
												<Text fontSize="md" mb={1}>
													{searchQuery ? "No chats found" : "No chats yet"}
												</Text>
												<Text fontSize="sm">
													{searchQuery
														? "Try a different search term"
														: "Start a new chat to begin"}
												</Text>
											</Flex>
										) : (
											<VStack spacing={2} align="stretch">
												{chatSessions.map((session) => {
													const isEditing = editingId === session.id;
													return (
														<Flex
															key={session.id}
															p={3}
															align="center"
															borderRadius="lg"
															border={selectedIds.has(session.id!) ? "2px solid" : "1px solid"}
															borderColor={selectedIds.has(session.id!) ? "gray.500" : "gray.300"}
															bg={
																selectedIds.has(session.id!)
																	? "purple.50"
																	: hoveredId === session.id
																		? "gray.50"
																		: "white"
															}
															cursor={isEditing ? "default" : "pointer"}
															transition="all 0.15s ease"
															onMouseEnter={() => setHoveredId(session.id!)}
															onMouseLeave={() => setHoveredId(null)}
															onClick={() => !isEditing && handleChatClick(session.id!)}
															role="group"
															tabIndex={0}
															_hover={{ borderColor: "gray.500" }}
														>
															{/* Checkbox */}
															<Box
																onClick={(e) => {
																	e.stopPropagation();
																	toggleSelection(session.id!);
																}}
																mr={3}
															>
																<Checkbox
																	isChecked={selectedIds.has(session.id!)}
																	onChange={() => toggleSelection(session.id!)}
																	colorScheme="purple"
																	size="md"
																/>
															</Box>

															{/* Chat Info */}
															<Box flex="1" minW={0}>
																{isEditing ? (
																	<Input
																		size="sm"
																		autoFocus
																		value={editValue}
																		onChange={(e) => setEditValue(e.target.value)}
																		onKeyDown={(e) => {
																			if (e.key === "Enter") handleRename(session.id!);
																			if (e.key === "Escape") setEditingId(null);
																		}}
																		onBlur={() => setEditingId(null)}
																		onClick={(e) => e.stopPropagation()}
																		bg="white"
																		color="fg.default"
																		border="1px solid"
																		borderColor="gray.300"
																		borderRadius="md"
																		fontSize="sm"
																	/>
																) : (
																	<>
																		<Text
																			fontSize="md"
																			fontWeight="600"
																			color="fg.default"
																			noOfLines={1}
																			align="left"
																			m={0}
																		>
																			{session.name || "Untitled"}
																		</Text>
																		<Text fontSize="sm" color="gray.500" align="left" m={0} mt={1}>
																			{formatLastModified(session.last_modified_date)}
																		</Text>
																	</>
																)}
															</Box>

															{/* Actions Menu */}
															{!isEditing && (
																<Menu placement="bottom-start">
																	<MenuButton
																		as={IconButton}
																		icon={<Icon as={ChevronDownIcon} boxSize={5} />}
																		size="xs"
																		variant="ghost"
																		color="purple.700"
																		_hover={{ bg: "purple.700", color: "white" }}
																		aria-label="Chat options"
																		onClick={(e) => e.stopPropagation()}
																		opacity={0}
																		_groupHover={{ opacity: 1 }}
																		transition="opacity 0.2s"
																	/>
																	<MenuList minW="120px" boxShadow="lg" zIndex={1001}>
																		<MenuItem
																			icon={<FiEdit2 size={14} />}
																			fontSize="sm"
																			onClick={(e) => {
																				e.stopPropagation();
																				setEditValue(session.name || "");
																				setEditingId(session.id!);
																			}}
																		>
																			Rename
																		</MenuItem>
																		<MenuItem
																			icon={<FiTrash2 size={14} />}
																			fontSize="sm"
																			color="red.500"
																			onClick={(e) => {
																				e.stopPropagation();
																				deleteChatSession(session.id!).then(() => {
																					setChatSessions(prev => prev.filter(s => s.id !== session.id));
																					setTotalChats(prev => prev - 1);
																					// Remove from context list if present (keeps Navbar in sync)
																					setChatSessionsList(prev => prev.filter(s => s.id !== session.id));
																				});
																			}}
																		>
																			Delete
																		</MenuItem>
																	</MenuList>
																</Menu>
															)}
														</Flex>
													);
												})}

												{/* Load More Button - show when count is multiple of 50 */}
												{chatSessions.length > 0 && chatSessions.length % 50 === 0 && (
													<Button
														variant="outline"
														colorScheme="purple"
														size="sm"
														mt={2}
														isLoading={isLoadingMore}
														loadingText="Loading..."
														onClick={handleLoadMore}
													>
														Load More
													</Button>
												)}
											</VStack>
										)}
									</Box>
								</Box>
							</TabPanel>

							{/* Analyses Tab */}
							<TabPanel px={0} py={0} flex={1} display="flex" flexDirection="column" minH={0}>
								<AnalysesTab isTabActive={tabIndex === 1 ? true : false} />
							</TabPanel>

							{/* Scheduled Runs Tab */}
							{/* <TabPanel px={0} py={0}>
								<Box
									h="calc(100vh - 200px)"
									overflowY="hidden"
								>
									<ScheduledRuns
										analystsList={analystsList}
										isTabActive={tabIndex === 2 ? true : false}
									/>
								</Box>
							</TabPanel> */}

							{/* Analysis Outputs Tab */}
							<TabPanel px={0} py={0}>
								<Box
									h="calc(100vh - 200px)"
									overflowY="hidden"
								>
									<AnalysisRun
										analystsList={analystsList}
										isTabActive={tabIndex === 2 ? true : false}
									/>
								</Box>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Box>
			</Flex>
		</Box>
	);
};

export default ChatSessionPage;
